import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, LogOut, RefreshCcw, Users, Loader2, MessageCircle, RotateCcw, Check, RotateCw, ExternalLink, Copy, CheckCheck, Eye, EyeOff, Lock, Paperclip, Send, Sparkles, X } from "lucide-react";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { listLeads } from "@/lib/leads.functions";
import { listAplicacaoLeads, setAplicacaoEnviado } from "@/lib/aplicacao-leads.functions";
import { listCompras, retryCompraPostback } from "@/lib/compras.functions";
import { listMetricas, upsertMetrica, deleteMetrica, unlockMetricas, upsertMetricasBulk, parseInvestmentMessage } from "@/lib/metricas.functions";
import { listCrmEvents, resendCrmEvent, resendFailedCrmEvents } from "@/lib/crm-events.functions";
import { getPageViewStats } from "@/lib/pageview-stats.functions";
import { supabase } from "@/integrations/supabase/client";
import { finalBoasVindas, saudacaoPorHorario } from "@/lib/gender";

const brl = (v: number | null | undefined) =>
  v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });



// ---------- WhatsApp helpers ----------
type WaInfo = {
  digits: string;
  display: string;
  isIntl: boolean;
  valid: boolean;
};

function parseWhatsapp(raw?: string | null): WaInfo {
  const original = (raw ?? "").trim();
  const hadPlus = original.startsWith("+");
  const digits = original.replace(/\D/g, "");
  if (!digits || digits.length < 8) {
    return { digits: "", display: original || "—", isIntl: false, valid: false };
  }
  let final = digits;
  let isIntl = false;
  if (hadPlus) {
    final = digits;
    isIntl = !digits.startsWith("55");
  } else if (digits.length === 10 || digits.length === 11) {
    final = "55" + digits;
  } else if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    final = digits;
  } else {
    final = digits;
    isIntl = !digits.startsWith("55");
  }
  return { digits: final, display: formatWaDisplay(final, isIntl), isIntl, valid: true };
}

function formatWaDisplay(d: string, isIntl: boolean) {
  if (!isIntl && d.startsWith("55") && (d.length === 12 || d.length === 13)) {
    const ddd = d.slice(2, 4);
    const rest = d.slice(4);
    const mid = rest.length === 9 ? rest.slice(0, 5) : rest.slice(0, 4);
    const end = rest.length === 9 ? rest.slice(5) : rest.slice(4);
    return `+55 (${ddd}) ${mid}-${end}`;
  }
  // Internacional: +cc resto
  const cc = d.slice(0, d.length > 11 ? 2 : 1);
  return `+${cc} ${d.slice(cc.length)}`;
}

function firstName(name?: string | null) {
  const n = (name ?? "").trim();
  if (!n) return "";
  return n.split(/\s+/)[0];
}

function waLink(digits: string, message: string) {
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

const MSG_RECUPERACAO = (nome: string, name: string, createdAt: string) =>
  `${saudacaoPorHorario(createdAt)} ${nome}! Tudo bem?\n\nAqui é a Jusy ✨\n\nDr. Corassa me pediu pra entrar em contato porque vimos que você iniciou o preenchimento dos dados para aproveitar a condição exclusiva do Saúde Máxima com 2000 reais de desconto.\n\nComo posso te ajudar? ${finalBoasVindas(name)} 🥰🙏`;


const MSG_FOLLOWUP = (nome: string) =>
  `Oii ${nome}, tudo bem? Queria saber se deu tudo certo com sua inscrição.\n\nQualquer coisa estou aqui, pode me chamar 🥰`;


export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard de Leads — Cura pela Alimentação" }] }),
  component: Dashboard,
});

type Tab = "masterclass" | "aplicacao" | "compras" | "metricas" | "crm";

function Dashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("masterclass");

  const fetchLeads = useServerFn(listLeads);
  const fetchAplic = useServerFn(listAplicacaoLeads);
  const fetchCompras = useServerFn(listCompras);
  const fetchMetricas = useServerFn(listMetricas);
  const fetchCrm = useServerFn(listCrmEvents);

  const masterclass = useQuery({
    queryKey: ["leads"],
    queryFn: () => fetchLeads(),
  });
  const aplicacao = useQuery({
    queryKey: ["aplicacao_leads"],
    queryFn: () => fetchAplic(),
  });
  const compras = useQuery({
    queryKey: ["compras"],
    queryFn: () => fetchCompras(),
  });
  const lock = useSpendLock();
  const metricas = useQuery({
    queryKey: ["metricas", lock.token ?? "locked"],
    queryFn: () => fetchMetricas({ data: { token: lock.token } }),
  });
  const crm = useQuery({
    queryKey: ["crm_events"],
    queryFn: () => fetchCrm(),
  });

  const onSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  const active =
    tab === "masterclass"
      ? masterclass
      : tab === "aplicacao"
        ? aplicacao
        : tab === "compras"
          ? compras
          : tab === "crm"
            ? crm
            : metricas;
  const refetch = () => active.refetch();


  return (
    <main className="relative min-h-screen px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-leaf/15 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
              Cura pela Alimentação
            </p>
            <h1 className="mt-1 font-display text-3xl font-bold uppercase text-cream sm:text-4xl">
              Painel de Leads
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refetch}
              disabled={active.isFetching}
              className="inline-flex items-center gap-2 rounded-xl border border-leaf/40 bg-leaf/10 px-3 py-2 text-xs font-semibold text-cream transition hover:bg-leaf/20 disabled:opacity-50"
            >
              <RefreshCcw className={`size-4 ${active.isFetching ? "animate-spin" : ""}`} />
              Atualizar
            </button>
            <button
              onClick={onSignOut}
              className="inline-flex items-center gap-2 rounded-xl border border-leaf/40 bg-leaf/10 px-3 py-2 text-xs font-semibold text-cream transition hover:bg-leaf/20"
            >
              <LogOut className="size-4" />
              Sair
            </button>
          </div>
        </header>

        <div className="mt-6 -mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
          <div className="inline-flex min-w-max rounded-2xl border border-leaf/20 bg-card/60 p-1">
            <TabButton active={tab === "masterclass"} onClick={() => setTab("masterclass")}>
              Masterclass{" "}
              <span className="ml-2 rounded-full bg-leaf/20 px-2 py-0.5 text-[10px] text-leaf-bright">
                {masterclass.data?.totalCount ?? masterclass.data?.leads.length ?? 0}
              </span>
            </TabButton>
            <TabButton active={tab === "aplicacao"} onClick={() => setTab("aplicacao")}>
              Aplicações{" "}
              <span className="ml-2 rounded-full bg-leaf/20 px-2 py-0.5 text-[10px] text-leaf-bright">
                {aplicacao.data?.totalCount ?? aplicacao.data?.leads.length ?? 0}
              </span>
            </TabButton>
            <TabButton active={tab === "compras"} onClick={() => setTab("compras")}>
              Compras{" "}
              <span className="ml-2 rounded-full bg-leaf/20 px-2 py-0.5 text-[10px] text-leaf-bright">
                {compras.data?.totalCount ?? compras.data?.compras.length ?? 0}
              </span>
            </TabButton>
            <TabButton active={tab === "crm"} onClick={() => setTab("crm")}>
              CRM Meta{" "}
              <span className="ml-2 rounded-full bg-leaf/20 px-2 py-0.5 text-[10px] text-leaf-bright">
                {crm.data?.totalCount ?? 0}
              </span>
            </TabButton>
            <TabButton active={tab === "metricas"} onClick={() => setTab("metricas")}>
              Métricas
            </TabButton>
          </div>
        </div>

        {tab === "masterclass" ? (
          <MasterclassPanel query={masterclass} />
        ) : tab === "aplicacao" ? (
          <AplicacaoPanel query={aplicacao} />
        ) : tab === "compras" ? (
          <ComprasPanel query={compras} />
        ) : tab === "crm" ? (
          <CrmPanel query={crm} />
        ) : (
          <MetricasPanel query={metricas} lock={lock} />
        )}


        <p className="mt-4 text-center text-[11px] text-muted-foreground">
          Acesso restrito a administradores.
        </p>
      </div>
    </main>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-xl px-4 py-2 text-xs font-semibold transition ${
        active
          ? "bg-leaf-bright text-deep"
          : "text-cream/70 hover:bg-leaf/10 hover:text-cream"
      }`}
    >
      {children}
    </button>
  );
}

type MasterQuery = ReturnType<typeof useQuery<Awaited<ReturnType<typeof listLeads>>>>;
type AplicQuery = ReturnType<typeof useQuery<Awaited<ReturnType<typeof listAplicacaoLeads>>>>;

/** Cobertura dos identificadores de tracking nas linhas carregadas. */
function TrackingCoverage({
  rows,
}: {
  rows: Array<{ fbc?: string | null; fbp?: string | null; clickid?: string | null }>;
}) {
  if (rows.length === 0) return null;
  const pct = (n: number) => `${Math.round((n / rows.length) * 100)}%`;
  const withFbc = rows.filter((r) => !!r.fbc).length;
  const withFbp = rows.filter((r) => !!r.fbp).length;
  const withClick = rows.filter((r) => !!r.clickid).length;
  const item = (label: string, n: number) => (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-cream/70">{label}</span>
      <span className="font-semibold text-cream">
        {pct(n)} <span className="text-xs font-normal text-cream/50">({n}/{rows.length})</span>
      </span>
    </div>
  );
  return (
    <div
      className="mt-4 rounded-2xl bg-card p-4 text-sm"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <p className="mb-2 text-xs uppercase tracking-wide text-cream/50">
        Cobertura de tracking (registros carregados)
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        {item("com fbc (clique Meta)", withFbc)}
        {item("com fbp", withFbp)}
        {item("com clickid (RedTrack)", withClick)}
      </div>
    </div>
  );
}


/** PageViews enviados ao Meta pela API de Conversões (últimos 7 dias). */
function PageViewCapiPanel() {
  const fetchStats = useServerFn(getPageViewStats);
  const { data, isLoading } = useQuery({
    queryKey: ["pageview-stats"],
    queryFn: () => fetchStats({}),
    staleTime: 60_000,
  });

  const pct = (n: number, total: number) => (total > 0 ? `${Math.round((n / total) * 100)}%` : "—");

  return (
    <div className="mt-4 rounded-2xl bg-card p-4 text-sm" style={{ boxShadow: "var(--shadow-card)" }}>
      <p className="mb-2 text-xs uppercase tracking-wide text-cream/50">
        PageView via API de Conversões (7 dias)
      </p>
      {isLoading ? (
        <p className="text-cream/60">Carregando…</p>
      ) : !data || data.total === 0 ? (
        <p className="text-cream/60">Nenhum PageView registrado ainda.</p>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-4">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-cream/70">Enviados</span>
              <span className="font-semibold text-cream">{data.sent}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-cream/70">Falhas</span>
              <span className="font-semibold text-cream">{data.errors}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-cream/70">com fbc</span>
              <span className="font-semibold text-cream">{pct(data.withFbc, data.total)}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-cream/70">com fbp</span>
              <span className="font-semibold text-cream">{pct(data.withFbp, data.total)}</span>
            </div>
          </div>
          <div className="mt-3 space-y-1 border-t border-leaf/15 pt-3 text-xs">
            {data.byDay.map((d) => (
              <div key={d.date} className="flex items-baseline justify-between gap-2">
                <span className="text-cream/60">{d.date.split("-").reverse().slice(0, 2).join("/")}</span>
                <span className="text-cream/80">
                  {d.sent} enviados
                  {d.errors > 0 ? ` · ${d.errors} falhas` : ""} · {pct(d.withFbc, d.total)} com fbc
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function MasterclassPanel({ query }: { query: MasterQuery }) {
  const { data, isLoading, error } = query;
  const leads = data?.leads ?? [];
  const totalCount = data?.totalCount ?? leads.length;
  const last7Count = data?.last7Count ?? 0;

  const onExport = () => {
    exportCsv("masterclass-leads", leads, [
      ["email", "E-mail"],
      ["created_at", "Data"],
      ["source", "Origem"],
      ["clickid", "ClickID"],
      ["clickid_source", "ClickID Source"],
      ["postback_status", "Postback"],
      ["postback_attempts", "Tentativas Postback"],
      ["postback_last_error", "Erro Postback"],
      ["meta_capi_status", "Meta CAPI"],
      ["meta_capi_error", "Erro Meta CAPI"],
      ["fbp", "fbp"],
      ["fbc", "fbc"],
      ["external_id", "external_id"],
      ["meta_event_id", "meta_event_id"],
      ["ip", "IP"],
      ["user_agent", "User Agent"],
    ]);
  };

  return (
    <>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Total de inscritos" value={totalCount} icon={<Users className="size-5" />} />
        <Stat label="Hoje" value={leads.filter((l) => isToday(new Date(l.created_at))).length} />
        <Stat label="Últimos 7 dias" value={last7Count} />
      </div>

      <TrackingCoverage rows={leads} />

      <PageViewCapiPanel />


      <div className="mt-4 flex justify-end">
        <ExportButton onClick={onExport} disabled={leads.length === 0} />
      </div>


      <section
        className="mt-6 overflow-hidden rounded-3xl bg-card"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : leads.length === 0 ? (
          <EmptyState>Nenhum lead ainda. Quando alguém se inscrever, aparece aqui.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-leaf/20 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">E-mail</th>
                  <th className="px-4 py-3 font-semibold">Data</th>
                  <th className="px-4 py-3 font-semibold">Origem</th>
                  <th className="px-4 py-3 font-semibold">ClickID</th>
                  <th className="px-4 py-3 font-semibold">Postback RedTrack</th>
                  <th className="px-4 py-3 font-semibold">Meta CAPI</th>
                  <th className="px-4 py-3 font-semibold">fbp / fbc</th>
                  <th className="px-4 py-3 font-semibold">IP</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-leaf/10 last:border-0 hover:bg-leaf/5">
                    <td className="px-4 py-3 font-medium text-cream">{l.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="px-4 py-3 text-[10px] uppercase tracking-wider text-gold">
                      {l.source}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {l.clickid ? (
                        <span className="inline-flex items-center gap-1.5" title={`${l.clickid} (${l.clickid_source ?? "?"})`}>
                          {String(l.clickid).slice(0, 12)}…
                          <CopyButton text={l.clickid} />
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={l.postback_status}
                        title={l.postback_last_error || l.postback_last_response || undefined}
                      />
                      {l.postback_attempts > 0 && (
                        <span className="ml-1 text-[10px] text-muted-foreground">
                          ×{l.postback_attempts}
                        </span>
                      )}
                      <PostbackUrlLink url={l.postback_last_url} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={l.meta_capi_status}
                        title={l.meta_capi_error || undefined}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {l.fbp ? "fbp" : "—"} / {l.fbc ? "fbc" : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {l.ip ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function AplicacaoPanel({ query }: { query: AplicQuery }) {
  const { data, isLoading, error } = query;
  const allLeads = data?.leads ?? [];
  const [phoneFilter, setPhoneFilter] = useState<"all" | "br" | "intl">("all");

  const queryClient = useQueryClient();
  const setEnviadoFn = useServerFn(setAplicacaoEnviado);
  const toggleMutation = useMutation({
    mutationFn: (vars: { id: string; enviado: boolean }) =>
      setEnviadoFn({ data: vars }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["aplicacao_leads"] });
      const prev = queryClient.getQueryData<{ leads: Array<Record<string, unknown>> }>([
        "aplicacao_leads",
      ]);
      if (prev) {
        queryClient.setQueryData(["aplicacao_leads"], {
          ...prev,
          leads: prev.leads.map((l) =>
            (l as { id: string }).id === vars.id ? { ...l, enviado: vars.enviado } : l,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["aplicacao_leads"], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["aplicacao_leads"] });
    },
  });

  const leadsWithWa = useMemo(
    () => allLeads.map((l) => ({ lead: l, wa: parseWhatsapp(l.phone) })),
    [allLeads],
  );

  const leads = useMemo(() => {
    if (phoneFilter === "all") return leadsWithWa;
    if (phoneFilter === "br") return leadsWithWa.filter((x) => !x.wa.isIntl);
    return leadsWithWa.filter((x) => x.wa.isIntl);
  }, [leadsWithWa, phoneFilter]);


  const onExport = () => {
    exportCsv("aplicacao-leads", allLeads, [
      ["name", "Nome"],
      ["enviado", "Enviado"],
      ["precheckout_notify_status", "Webhook pré-checkout"],
      ["precheckout_notify_tipo", "Comprou?"],
      ["created_at", "Data"],
      ["phone", "WhatsApp"],
      ["email", "E-mail"],
      ["clickid", "ClickID"],
      ["clickid_source", "ClickID Source"],
      ["postback_status", "Postback"],
      ["postback_attempts", "Tentativas Postback"],
      ["postback_last_error", "Erro Postback"],
      ["meta_capi_status", "Meta CAPI"],
      ["meta_capi_error", "Erro Meta CAPI"],
      ["datacrazy_status", "DataCrazy"],
      ["datacrazy_last_error", "Erro DataCrazy"],
      ["fbp", "fbp"],
      ["fbc", "fbc"],
      ["external_id", "external_id"],
      ["meta_event_id", "meta_event_id"],
      ["ip", "IP"],
      ["user_agent", "User Agent"],
      ["phone_ddd", "DDD"],
      ["phone_number", "Número"],
      ["source", "Origem"],
    ]);
  };

  const totalCount = data?.totalCount ?? allLeads.length;
  const last7Count = data?.last7Count ?? 0;

  return (
    <>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Total de aplicações" value={totalCount} icon={<Users className="size-5" />} />
        <Stat label="Hoje" value={allLeads.filter((l) => isToday(new Date(l.created_at))).length} />
        <Stat label="Últimos 7 dias" value={last7Count} />
      </div>

      <TrackingCoverage rows={allLeads} />


      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-leaf/20 bg-card/60 p-1 text-[11px]">
          {([
            ["all", "Todos"],
            ["br", "Brasil"],
            ["intl", "Internacional"],
          ] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setPhoneFilter(k)}
              className={`rounded-lg px-3 py-1.5 font-semibold transition ${
                phoneFilter === k
                  ? "bg-leaf-bright text-deep"
                  : "text-cream/70 hover:bg-leaf/10 hover:text-cream"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <ExportButton onClick={onExport} disabled={allLeads.length === 0} />
      </div>

      <section
        className="mt-6 overflow-hidden rounded-3xl bg-card"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : leads.length === 0 ? (
          <EmptyState>Nenhuma aplicação ainda. Quando alguém preencher o formulário em /aplicacao, aparece aqui.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-leaf/20 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">Nome</th>
                  <th className="px-4 py-3 font-semibold">Ações</th>
                  <th className="px-4 py-3 font-semibold">Enviado</th>
                  <th className="px-4 py-3 font-semibold">Data</th>
                  <th className="px-4 py-3 font-semibold">WhatsApp</th>
                  <th className="px-4 py-3 font-semibold">E-mail</th>
                  <th className="px-4 py-3 font-semibold">ClickID</th>
                  <th className="px-4 py-3 font-semibold">Postback</th>
                  <th className="px-4 py-3 font-semibold">Meta CAPI</th>
                  <th className="px-4 py-3 font-semibold">DataCrazy</th>
                  <th className="px-4 py-3 font-semibold">fbp / fbc</th>
                  <th className="px-4 py-3 font-semibold">IP</th>

                </tr>
              </thead>
              <tbody>
                {leads.map(({ lead: l, wa }) => {
                  const nome = firstName(l.name);
                  return (
                  <tr key={l.id} className="border-b border-leaf/10 last:border-0 hover:bg-leaf/5">
                    <td className="px-4 py-3 font-medium text-cream">
                      <span className="inline-flex items-center gap-2">
                        {l.name}
                        {wa.isIntl && (
                          <span className="rounded-full border border-gold/40 bg-gold/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-gold">
                            INTL
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <WaButton
                          variant="primary"
                          disabled={!wa.valid}
                          href={wa.valid ? waLink(wa.digits, MSG_RECUPERACAO(nome, l.name, l.created_at)) : undefined}
                          icon={<MessageCircle className="size-3.5" />}
                          label="Recuperação"
                        />
                        <WaButton
                          variant="outline"
                          disabled={!wa.valid}
                          href={wa.valid ? waLink(wa.digits, MSG_FOLLOWUP(nome)) : undefined}
                          icon={<RotateCcw className="size-3.5" />}
                          label="Follow-up"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <EnviadoToggle
                          checked={
                            Boolean((l as { enviado?: boolean }).enviado) ||
                            (l as { precheckout_notify_status?: string }).precheckout_notify_status === "sent"
                          }
                          onToggle={(next) =>
                            toggleMutation.mutate({ id: l.id, enviado: next })
                          }
                        />
                        <CompraBadge
                          status={(l as { precheckout_notify_status?: string | null }).precheckout_notify_status ?? null}
                          tipo={(l as { precheckout_notify_tipo?: string | null }).precheckout_notify_tipo ?? null}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                      {wa.display}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{l.email}</td>



                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {l.clickid ? (
                        <span className="inline-flex items-center gap-1.5" title={`${l.clickid} (${l.clickid_source ?? "?"})`}>
                          {String(l.clickid).slice(0, 12)}…
                          <CopyButton text={l.clickid} />
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={l.postback_status}
                        title={l.postback_last_error || l.postback_last_response || undefined}
                      />
                      {l.postback_attempts > 0 && (
                        <span className="ml-1 text-[10px] text-muted-foreground">
                          ×{l.postback_attempts}
                        </span>
                      )}
                      <PostbackUrlLink url={l.postback_last_url} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={l.meta_capi_status}
                        title={l.meta_capi_error || undefined}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={(l as { datacrazy_status?: string }).datacrazy_status ?? "pending"}
                        title={
                          (l as { datacrazy_last_error?: string | null }).datacrazy_last_error ||
                          (l as { datacrazy_last_response?: string | null }).datacrazy_last_response ||
                          undefined
                        }
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {l.fbp ? "fbp" : "—"} / {l.fbc ? "fbc" : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {l.ip ?? "—"}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

type ComprasQuery = ReturnType<typeof useQuery<Awaited<ReturnType<typeof listCompras>>>>;

function ComprasPanel({ query }: { query: ComprasQuery }) {
  const { data, isLoading, error, refetch } = query;
  const compras = data?.compras ?? [];
  const queryClient = useQueryClient();
  const retryFn = useServerFn(retryCompraPostback);
  const retryMutation = useMutation({
    mutationFn: (id: string) => retryFn({ data: { id } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["compras"] }),
  });

  const onExport = () => {
    exportCsv("compras", compras, [
      ["created_at", "Data"],
      ["buyer_name", "Nome"],
      ["buyer_email", "E-mail"],
      ["buyer_phone", "Telefone"],
      ["hotmart_transaction", "Transação Hotmart"],
      ["product_name", "Produto"],
      ["offer_code", "Oferta"],
      ["price_value", "Valor"],
      ["price_currency", "Moeda"],
      ["status", "Status"],
      ["event_type", "Evento"],
      ["clickid", "ClickID"],
      ["clickid_source", "ClickID Source"],
      ["postback_status", "Postback"],
      ["postback_attempts", "Tentativas Postback"],
      ["postback_last_error", "Erro Postback"],
    ]);
  };

  const totalValue = data?.totalRevenue ?? compras.reduce((sum, c) => sum + (Number(c.price_value) || 0), 0);
  const totalCount = data?.totalCount ?? compras.length;
  const todayCount = data?.todayCount ?? compras.filter((c) => isToday(new Date(c.created_at))).length;
  const FATURAMENTO_HISTORICO_BRL = 41159.72;
  const faturamentoProjeto = totalValue + FATURAMENTO_HISTORICO_BRL;

  return (
    <>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total de compras" value={totalCount} icon={<Users className="size-5" />} />
        <Stat label="Hoje" value={todayCount} />
        <Stat label="Faturado (diário)" value={totalValue} formatter={brl} />
        <Stat label="Faturamento (total)" value={faturamentoProjeto} formatter={brl} />


      </div>

      <div className="mt-4 flex justify-end">
        <ExportButton onClick={onExport} disabled={compras.length === 0} />
      </div>

      <section
        className="mt-6 overflow-hidden rounded-3xl bg-card"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : compras.length === 0 ? (
          <EmptyState>
            Nenhuma compra ainda. Configure o webhook da Hotmart apontando para <code>/api/public/hotmart</code>.
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-leaf/20 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">Data</th>
                  <th className="px-4 py-3 font-semibold">Nome</th>
                  <th className="px-4 py-3 font-semibold">E-mail</th>
                  <th className="px-4 py-3 font-semibold">Produto</th>
                  <th className="px-4 py-3 font-semibold">Valor</th>
                  <th className="px-4 py-3 font-semibold">Transação</th>
                  <th className="px-4 py-3 font-semibold">ClickID</th>
                  <th className="px-4 py-3 font-semibold">Postback RedTrack</th>
                  <th className="px-4 py-3 font-semibold">Ação</th>
                </tr>
              </thead>
              <tbody>
                {compras.map((c) => (
                  <tr key={c.id} className="border-b border-leaf/10 last:border-0 hover:bg-leaf/5">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {formatDate(c.created_at)}
                    </td>
                    <td className="px-4 py-3 font-medium text-cream">{c.buyer_name ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.buyer_email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.product_name ?? "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-leaf-bright">
                      {brl(c.price_value)}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {c.hotmart_transaction ?? "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                      {c.clickid ? (
                        <span title={`${c.clickid} (${c.clickid_source ?? "?"})`}>
                          {String(c.clickid).slice(0, 12)}…
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={c.postback_status}
                        title={c.postback_last_error || c.postback_last_response || undefined}
                      />
                      {c.postback_attempts > 0 && (
                        <span className="ml-1 text-[10px] text-muted-foreground">
                          ×{c.postback_attempts}
                        </span>
                      )}
                      <PostbackUrlLink url={c.postback_last_url} showUrl />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => retryMutation.mutate(c.id)}
                        disabled={retryMutation.isPending && retryMutation.variables === c.id}
                        title="Reenviar postback pro RedTrack"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-leaf/40 bg-leaf/10 px-2 py-1 text-[11px] font-semibold text-cream transition hover:bg-leaf/20 disabled:opacity-50"
                      >
                        <RotateCw
                          className={`size-3.5 ${
                            retryMutation.isPending && retryMutation.variables === c.id
                              ? "animate-spin"
                              : ""
                          }`}
                        />
                        Reenviar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {/* keep refetch reference to satisfy lint */}
      <span className="hidden" data-refetch={String(Boolean(refetch))} />
    </>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // ignore
        }
      }}
      title={copied ? "Copiado!" : "Copiar ClickID"}
      className="inline-flex items-center rounded border border-leaf/30 bg-leaf/5 p-0.5 text-cream transition hover:bg-leaf/15"
    >
      {copied ? <CheckCheck className="size-3 text-leaf-bright" /> : <Copy className="size-3" />}
    </button>
  );
}

function PostbackUrlLink({ url, showUrl }: { url?: string | null; showUrl?: boolean }) {
  if (!url) return null;
  const display = showUrl ? `${url.slice(0, 40)}${url.length > 40 ? "…" : ""}` : "URL";
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      onClick={(e) => e.stopPropagation()}
      className="ml-1 inline-flex items-center gap-0.5 rounded border border-leaf/30 bg-leaf/5 px-1.5 py-0.5 text-[9px] font-medium text-sky-300 hover:bg-leaf/15 hover:text-sky-200"
    >
      <ExternalLink className="size-3" />
      {display}
    </a>
  );
}

function WaButton({
  href,
  disabled,
  icon,
  label,
  variant,
}: {
  href?: string;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  variant: "primary" | "outline";
}) {
  const base =
    "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-leaf-bright text-deep hover:brightness-110"
      : "border border-sky-400/40 bg-sky-400/10 text-sky-300 hover:bg-sky-400/20";
  if (disabled || !href) {
    return (
      <button type="button" disabled className={`${base} ${styles}`} title="Número inválido">
        {icon}
        <span>{label}</span>
      </button>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${base} ${styles}`}
    >
      {icon}
      <span>{label}</span>
    </a>
  );
}

function EnviadoToggle({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!checked)}
      aria-pressed={checked}
      title={checked ? "Marcado como enviado" : "Marcar como enviado"}
      className={`grid size-6 place-items-center rounded-full border transition ${
        checked
          ? "border-leaf-bright bg-leaf-bright text-deep"
          : "border-leaf/40 bg-transparent text-transparent hover:bg-leaf/10"
      }`}
    >
      <Check className="size-3.5" strokeWidth={3} />
    </button>
  );
}

/** Mostra se o lead já constava como compra no momento do envio do webhook. */
function CompraBadge({
  status,
  tipo,
}: {
  status: string | null;
  tipo: string | null;
}) {
  if (status !== "sent" || !tipo) {
    return (
      <span
        className="rounded-full border border-leaf/25 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60"
        title="Ainda sem verificação de compra"
      >
        —
      </span>
    );
  }
  const comprou = tipo === "compra";
  return (
    <span
      title={comprou ? "Já constava como compra" : "Não comprou até a verificação"}
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
        comprou
          ? "border border-leaf-bright/50 bg-leaf-bright/15 text-leaf-bright"
          : "border border-gold/40 bg-gold/10 text-gold"
      }`}
    >
      {comprou ? "Comprou" : "Não comprou"}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-3 px-6 py-16 text-sm text-muted-foreground">
      <Loader2 className="size-5 animate-spin" /> Carregando…
    </div>
  );
}
function ErrorState({ error }: { error: unknown }) {
  return (
    <div className="px-6 py-10 text-center text-sm text-destructive-foreground">
      {error instanceof Error ? error.message : "Erro ao carregar."}
    </div>
  );
}
function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-6 py-16 text-center text-sm text-muted-foreground">{children}</div>
  );
}

function StatusBadge({ status, title }: { status?: string | null; title?: string }) {
  const s = (status ?? "pending").toLowerCase();
  const tone =
    s === "sent"
      ? "bg-leaf/20 text-leaf-bright border-leaf/40"
      : s === "failed"
        ? "bg-red-500/15 text-red-300 border-red-500/40"
        : "bg-muted/30 text-muted-foreground border-muted/40";
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}
    >
      {s}
    </span>
  );
}

function Stat({
  label,
  value,
  icon,
  formatter,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
  formatter?: (v: number) => string;
}) {
  return (
    <div
      className="rounded-2xl bg-card px-5 py-4"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        {icon && <span className="text-gold">{icon}</span>}
      </div>
      <p className="mt-2 font-display text-3xl font-bold text-cream">
        {formatter ? formatter(value) : value}
      </p>
    </div>
  );
}

function isToday(d: Date) {
  const n = new Date();
  return (
    d.getDate() === n.getDate() &&
    d.getMonth() === n.getMonth() &&
    d.getFullYear() === n.getFullYear()
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ExportButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-xl border border-gold/40 bg-gold/10 px-3 py-2 text-xs font-semibold text-gold transition hover:bg-gold/20 disabled:opacity-50"
    >
      <Download className="size-4" />
      Exportar CSV
    </button>
  );
}

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return "";
  const s = typeof value === "string" ? value : String(value);
  if (/[",\n\r;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportCsv<T extends Record<string, unknown>>(
  baseName: string,
  rows: T[],
  columns: Array<[keyof T & string, string]>,
) {
  const header = columns.map(([, label]) => csvEscape(label)).join(",");
  const lines = rows.map((row) =>
    columns
      .map(([key]) => {
        const v = row[key];
        if (key === "created_at" && typeof v === "string") {
          return csvEscape(new Date(v).toISOString());
        }
        return csvEscape(v);
      })
      .join(","),
  );
  const csv = "\ufeff" + [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  a.href = url;
  a.download = `${baseName}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================
// MÉTRICAS
// ============================================================

type MetricasQuery = ReturnType<typeof useQuery<Awaited<ReturnType<typeof listMetricas>>>>;

type MetricEntry = {
  id: string;
  date: string; // YYYY-MM-DD
  ad_spend: number;
  leads_count: number | null;
  cpl: number | null;
  notes: string | null;
};

type FilterKind = "total" | "ano" | "mes" | "semana" | "dow" | "range";

const DOW_LABELS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const MONTH_LABELS = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];

// Parse "YYYY-MM-DD" como data LOCAL (evita off-by-one)
function parseDateKey(k: string): Date {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function fmtDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function todayBrtKey(): string {
  const d = new Date(Date.now() - 3 * 3600_000);
  return d.toISOString().slice(0, 10);
}
// Início da semana (segunda-feira)
function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day + 6) % 7; // segunda=0
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isoWeekLabel(d: Date): { key: string; label: string } {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const key = fmtDateKey(start);
  const fmt = (x: Date) =>
    `${String(x.getDate()).padStart(2, "0")}/${String(x.getMonth() + 1).padStart(2, "0")}`;
  return { key, label: `${fmt(start)} – ${fmt(end)}` };
}

// ---------- Bloqueio de visualização do investimento ----------

type SpendLock = {
  token: string | null;
  unlocked: boolean;
  unlock: (key: string) => Promise<boolean>;
  lockAgain: () => void;
  pending: boolean;
};

const LOCK_STORAGE_KEY = "metricas_unlock";

function useSpendLock(): SpendLock {
  const [token, setToken] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const unlockFn = useServerFn(unlockMetricas);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(LOCK_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { token: string; expiresAt: number };
      if (saved?.token && saved.expiresAt > Date.now()) setToken(saved.token);
      else sessionStorage.removeItem(LOCK_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const unlock = useCallback(
    async (key: string) => {
      setPending(true);
      try {
        const res = await unlockFn({ data: { key } });
        if (!res.ok) return false;
        setToken(res.token);
        try {
          sessionStorage.setItem(
            LOCK_STORAGE_KEY,
            JSON.stringify({ token: res.token, expiresAt: res.expiresAt }),
          );
        } catch {
          /* ignore */
        }
        return true;
      } finally {
        setPending(false);
      }
    },
    [unlockFn],
  );

  const lockAgain = useCallback(() => {
    setToken(null);
    try {
      sessionStorage.removeItem(LOCK_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return { token, unlocked: !!token, unlock, lockAgain, pending };
}

const HIDDEN = "••••";

function LockPrompt({ lock }: { lock: SpendLock }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const ok = await lock.unlock(key.trim());
    if (ok) {
      setOpen(false);
      setKey("");
    } else {
      setError("Chave inválida.");
    }
  };

  if (lock.unlocked) {
    return (
      <button
        onClick={lock.lockAgain}
        className="inline-flex items-center gap-1.5 rounded-xl border border-leaf/30 px-3 py-1.5 text-xs font-semibold text-cream/80 hover:bg-leaf/10"
        title="Ocultar investimento"
      >
        <Eye className="h-3.5 w-3.5" /> Investimento visível
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-xl border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs font-semibold text-gold-soft hover:bg-gold/20"
      >
        <EyeOff className="h-3.5 w-3.5" /> Investimento oculto
      </button>
      {open && (
        <div className="flex items-center gap-2">
          <div className="relative">
            <Lock className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="password"
              autoFocus
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Chave de acesso"
              className="w-44 rounded-lg border border-leaf/30 bg-deep/60 py-1.5 pl-7 pr-2 text-xs text-cream"
            />
          </div>
          <button
            onClick={submit}
            disabled={lock.pending || !key.trim()}
            className="rounded-lg bg-leaf-bright px-3 py-1.5 text-xs font-semibold text-deep disabled:opacity-50"
          >
            {lock.pending ? "..." : "Liberar"}
          </button>
          {error && <span className="text-xs text-red-300">{error}</span>}
        </div>
      )}
    </div>
  );
}

// ---------- Chat de IA para investimento ----------

type ChatMsg = { role: "user" | "ai"; text: string };

function SpendChat({ lock }: { lock: SpendLock }) {
  const qc = useQueryClient();
  const parseFn = useServerFn(parseInvestmentMessage);
  const bulkFn = useServerFn(upsertMetricasBulk);
  const fileRef = useRef<HTMLInputElement>(null);

  const [messages, setMessages] = useState<ChatMsg[]>([
    {
      role: "ai",
      text: "Me diga quanto investiu e em qual período (ex.: “investi 3.400 do dia 10 ao dia 30 de julho”) ou anexe o extrato do Facebook (print, CSV ou texto). Eu mostro a prévia antes de salvar.",
    },
  ]);
  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState<
    { kind: "image" | "text"; name: string; data: string } | null
  >(null);
  const [preview, setPreview] = useState<{ date: string; ad_spend: number }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickFile = async (file: File) => {
    setError(null);
    if (file.size > 6 * 1024 * 1024) {
      setError("Arquivo muito grande (máx. 6 MB).");
      return;
    }
    if (file.type.startsWith("image/")) {
      const data = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      setAttachment({ kind: "image", name: file.name, data });
    } else {
      const text = await file.text();
      setAttachment({ kind: "text", name: file.name, data: text.slice(0, 60000) });
    }
  };

  const send = async () => {
    if (busy) return;
    if (!input.trim() && !attachment) return;
    setError(null);
    setBusy(true);
    const userText = input.trim() || `(anexo: ${attachment?.name})`;
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setInput("");
    const att = attachment;
    setAttachment(null);
    try {
      const res = await parseFn({
        data: { token: lock.token!, message: userText, attachment: att },
      });
      if (!res.entries.length) {
        setPreview(null);
        setMessages((m) => [
          ...m,
          { role: "ai", text: res.note || "Não identifiquei valores de investimento." },
        ]);
      } else {
        setPreview(res.entries);
        const total = res.entries.reduce((a, e) => a + e.ad_spend, 0);
        setMessages((m) => [
          ...m,
          {
            role: "ai",
            text: `${res.note ? res.note + " " : ""}Encontrei ${res.entries.length} dia(s), total ${brl(total)}. Confira a prévia abaixo e confirme.`,
          },
        ]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const res = await bulkFn({ data: { token: lock.token!, entries: preview } });
      setPreview(null);
      setMessages((m) => [
        ...m,
        { role: "ai", text: `Salvo! ${res.count} dia(s) atualizados. ROAS recalculado.` },
      ]);
      qc.invalidateQueries({ queryKey: ["metricas"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const previewTotal = preview?.reduce((a, e) => a + e.ad_spend, 0) ?? 0;

  return (
    <div className="rounded-2xl border border-leaf/20 bg-card/60 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-gold" />
        <h3 className="text-sm font-semibold text-cream">Lançar investimento com IA</h3>
      </div>

      <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
              m.role === "user"
                ? "ml-auto bg-leaf/20 text-cream"
                : "bg-deep/60 text-cream/85"
            }`}
          >
            {m.text}
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> processando...
          </div>
        )}
      </div>

      {preview && (
        <div className="mt-3 rounded-xl border border-gold/30 bg-gold/5 p-3">
          <p className="mb-2 text-[11px] uppercase tracking-widest text-gold-soft">
            Prévia — total {brl(previewTotal)}
          </p>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-left text-xs">
              <tbody className="divide-y divide-border/30">
                {preview.map((e) => (
                  <tr key={e.date} className="text-cream/85">
                    <td className="py-1 pr-3">{e.date.split("-").reverse().join("/")}</td>
                    <td className="py-1">{brl(e.ad_spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={confirm}
              disabled={busy}
              className="rounded-lg bg-leaf-bright px-3 py-1.5 text-xs font-semibold text-deep disabled:opacity-50"
            >
              Confirmar e salvar
            </button>
            <button
              onClick={() => setPreview(null)}
              className="rounded-lg border border-border px-3 py-1.5 text-xs text-cream/70"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-300">{error}</p>}

      {attachment && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-leaf/30 bg-deep/50 px-2 py-1 text-[11px] text-cream/80">
          <Paperclip className="h-3 w-3" /> {attachment.name}
          <button onClick={() => setAttachment(null)} className="text-cream/60 hover:text-cream">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,text/*,.csv,.md,.txt"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void pickFile(f);
            e.target.value = "";
          }}
        />
        <button
          onClick={() => fileRef.current?.click()}
          className="rounded-lg border border-leaf/30 p-2 text-cream/80 hover:bg-leaf/10"
          title="Anexar extrato"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="Ex.: investi 3.400 do dia 10 ao dia 30 de julho"
          className="flex-1 rounded-lg border border-leaf/30 bg-deep/60 px-3 py-2 text-xs text-cream"
        />
        <button
          onClick={send}
          disabled={busy || (!input.trim() && !attachment)}
          className="rounded-lg bg-leaf-bright p-2 text-deep disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function MetricasPanel({ query, lock }: { query: MetricasQuery; lock: SpendLock }) {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertMetrica);
  const del = useServerFn(deleteMetrica);
  const upsertMut = useMutation({
    mutationFn: (input: Parameters<typeof upsert>[0]) => upsert(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metricas"] }),
  });
  const delMut = useMutation({
    mutationFn: (date: string) => del({ data: { date, token: lock.token } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metricas"] }),
  });

  const [filter, setFilter] = useState<FilterKind>("mes");
  const now = new Date();
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth()); // 0-11
  const [weekKey, setWeekKey] = useState<string>(fmtDateKey(startOfWeek(now)));
  const [dow, setDow] = useState<number>(1); // segunda
  const firstOfMonth = fmtDateKey(new Date(now.getFullYear(), now.getMonth(), 1));
  const [rangeStart, setRangeStart] = useState<string>(firstOfMonth);
  const [rangeEnd, setRangeEnd] = useState<string>(fmtDateKey(now));

  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;
  const data = query.data;
  if (!data) return <EmptyState>Sem dados.</EmptyState>;

  const unlocked = lock.unlocked && data.unlocked;
  const money = (v: number | null | undefined) => (unlocked ? brl(v) : HIDDEN);

  const entries: MetricEntry[] = data.entries as MetricEntry[];
  const salesByDay = data.salesByDay ?? {};

  const entryByDate = new Map<string, MetricEntry>();
  for (const e of entries) entryByDate.set(e.date, e);

  // Universo de datas conhecidas (entradas + vendas)
  const allDatesSet = new Set<string>();
  for (const e of entries) allDatesSet.add(e.date);
  for (const k of Object.keys(salesByDay)) allDatesSet.add(k);
  const allYears = Array.from(
    new Set(Array.from(allDatesSet).map((k) => Number(k.slice(0, 4))).filter(Boolean)),
  ).sort((a, b) => b - a);
  if (!allYears.includes(year)) allYears.unshift(year);

  // Datas do filtro corrente + label
  let filterLabel = "";
  let rowKeys: string[] = [];
  let groupBy: "day" | "month" = "day";

  if (filter === "total") {
    filterLabel = "Histórico completo";
    groupBy = "month";
    const months = new Set<string>();
    for (const k of allDatesSet) months.add(k.slice(0, 7));
    rowKeys = Array.from(months).sort().reverse();
  } else if (filter === "ano") {
    filterLabel = `${year}`;
    groupBy = "month";
    rowKeys = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
  } else if (filter === "mes") {
    filterLabel = `${MONTH_LABELS[month]} de ${year}`;
    const days = new Date(year, month + 1, 0).getDate();
    rowKeys = Array.from({ length: days }, (_, i) =>
      fmtDateKey(new Date(year, month, i + 1)),
    );
  } else if (filter === "semana") {
    const start = parseDateKey(weekKey);
    const { label } = isoWeekLabel(start);
    filterLabel = `Semana ${label}`;
    rowKeys = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return fmtDateKey(d);
    });
  } else if (filter === "range") {
    let a = rangeStart;
    let b = rangeEnd;
    if (a > b) [a, b] = [b, a];
    const fmtBr = (k: string) => k.split("-").reverse().join("/");
    filterLabel = `${fmtBr(a)} até ${fmtBr(b)}`;
    const start = parseDateKey(a);
    const end = parseDateKey(b);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      rowKeys.push(fmtDateKey(d));
      if (rowKeys.length > 400) break;
    }
    rowKeys.reverse();
  } else {
    // dow — todas as ocorrências desse dia da semana no mês
    filterLabel = `${DOW_LABELS[dow]}s de ${MONTH_LABELS[month]}/${year}`;
    const days = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= days; i++) {
      const d = new Date(year, month, i);
      if (d.getDay() === dow) rowKeys.push(fmtDateKey(d));
    }
  }

  // Cálculos
  function daySales(k: string): number {
    return salesByDay[k]?.revenue ?? 0;
  }
  function daySpend(k: string): number {
    return entryByDate.get(k)?.ad_spend ?? 0;
  }
  function dayCpl(k: string): number | null {
    return entryByDate.get(k)?.cpl ?? null;
  }
  function monthKeys(ym: string): string[] {
    const [y, m] = ym.split("-").map(Number);
    const days = new Date(y, m, 0).getDate();
    return Array.from({ length: days }, (_, i) =>
      `${ym}-${String(i + 1).padStart(2, "0")}`,
    );
  }

  const flatDayKeys = groupBy === "day" ? rowKeys : rowKeys.flatMap(monthKeys);
  const totalSpend = flatDayKeys.reduce((a, k) => a + daySpend(k), 0);
  const totalSales = flatDayKeys.reduce((a, k) => a + daySales(k), 0);
  const cplList = flatDayKeys.map((k) => dayCpl(k)).filter((v): v is number => v != null);
  const avgCpl = cplList.length ? cplList.reduce((a, b) => a + b, 0) / cplList.length : 0;
  const roas = totalSpend > 0 ? totalSales / totalSpend : 0;

  return (
    <section className="mt-4 space-y-4">
      {/* Filtros */}
      <div className="rounded-2xl border border-leaf/20 bg-card/60 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {(
            [
              ["mes", "Mês"],
              ["semana", "Semana"],
              ["ano", "Ano"],
              ["dow", "Dia da semana"],
              ["range", "Período"],
              ["total", "Total"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                filter === k
                  ? "bg-leaf-bright text-deep"
                  : "border border-leaf/30 text-cream/80 hover:bg-leaf/10"
              }`}
            >
              {label}
            </button>
          ))}

          <span className="mx-2 h-6 w-px bg-leaf/20" />

          {(filter === "ano" || filter === "mes" || filter === "dow") && (
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-lg border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
            >
              {allYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
          {(filter === "mes" || filter === "dow") && (
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="rounded-lg border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
            >
              {MONTH_LABELS.map((m, i) => (
                <option key={m} value={i}>{m}</option>
              ))}
            </select>
          )}
          {filter === "semana" && (
            <input
              type="date"
              value={weekKey}
              onChange={(e) => {
                const d = parseDateKey(e.target.value);
                setWeekKey(fmtDateKey(startOfWeek(d)));
              }}
              className="rounded-lg border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
            />
          )}
          {filter === "range" && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                className="rounded-lg border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
              />
              <span className="text-xs text-muted-foreground">até</span>
              <input
                type="date"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                className="rounded-lg border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
              />
            </div>
          )}
          {filter === "dow" && (
            <select
              value={dow}
              onChange={(e) => setDow(Number(e.target.value))}
              className="rounded-lg border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
            >
              {DOW_LABELS.map((d, i) => (
                <option key={d} value={i}>{d}</option>
              ))}
            </select>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {filterLabel}
          </p>
          <LockPrompt lock={lock} />
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Investimento" value={totalSpend} formatter={money} />
        <Stat label="Vendas" value={totalSales} formatter={brl} />
        <Stat label="CPL médio" value={avgCpl} formatter={money} />
        <Stat
          label="ROAS"
          value={roas}
          formatter={(v) => (unlocked ? `${v.toFixed(2)}x` : HIDDEN)}
        />
      </div>

      {unlocked ? (
        <SpendChat lock={lock} />
      ) : (
        <div className="flex items-center gap-2 rounded-2xl border border-gold/30 bg-gold/5 p-4 text-xs text-gold-soft">
          <Lock className="h-4 w-4" />
          Investimento, CPL, ROAS e o chat de lançamento ficam ocultos. Clique em “Investimento
          oculto” acima e informe a chave de acesso.
        </div>
      )}

      {/* Tabela */}
      <div
        className="overflow-hidden rounded-2xl bg-card"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-deep/50 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">{groupBy === "month" ? "Mês" : "Dia"}</th>
                <th className="px-4 py-3">Investimento</th>
                <th className="px-4 py-3">Vendas</th>
                <th className="px-4 py-3">CPL</th>
                <th className="px-4 py-3">ROAS</th>
                {groupBy === "day" && <th className="px-4 py-3 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {rowKeys.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Sem dados no período.
                  </td>
                </tr>
              )}
              {rowKeys.map((k) => {
                if (groupBy === "month") {
                  const days = monthKeys(k);
                  const spend = days.reduce((a, d) => a + daySpend(d), 0);
                  const sales = days.reduce((a, d) => a + daySales(d), 0);
                  const cpls = days.map((d) => dayCpl(d)).filter((v): v is number => v != null);
                  const cpl = cpls.length ? cpls.reduce((a, b) => a + b, 0) / cpls.length : 0;
                  const [y, m] = k.split("-").map(Number);
                  return (
                    <tr key={k} className="text-cream/90">
                      <td className="px-4 py-3 font-semibold">
                        {MONTH_LABELS[m - 1]}/{y}
                      </td>
                      <td className="px-4 py-3">{money(spend)}</td>
                      <td className="px-4 py-3">{brl(sales)}</td>
                      <td className="px-4 py-3">{unlocked ? (cpl ? brl(cpl) : "—") : HIDDEN}</td>
                      <td className="px-4 py-3">
                        {!unlocked ? HIDDEN : spend > 0 ? `${(sales / spend).toFixed(2)}x` : "—"}
                      </td>
                    </tr>
                  );
                }
                const entry = entryByDate.get(k);
                const spend = entry?.ad_spend ?? 0;
                const sales = daySales(k);
                const cpl = entry?.cpl ?? null;
                const d = parseDateKey(k);
                const dowLabel = DOW_LABELS[d.getDay()].slice(0, 3);
                const isToday = k === todayBrtKey();
                return (
                  <MetricRow
                    key={k}
                    dateKey={k}
                    dateLabel={`${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} · ${dowLabel}`}
                    highlight={isToday}
                    spend={spend}
                    sales={sales}
                    cpl={cpl}
                    unlocked={unlocked}
                    onSave={(patch) =>
                      upsertMut.mutate({ data: { date: k, token: lock.token, ...patch } })
                    }
                    onClear={() => delMut.mutate(k)}
                    saving={upsertMut.isPending}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Vendas são somadas automaticamente a partir da aba Compras (fuso GMT-3). O investimento
        pode ser lançado dia a dia na tabela ou por período no chat de IA.
      </p>
    </section>
  );
}

function MetricRow({
  dateKey,
  dateLabel,
  highlight,
  spend,
  sales,
  cpl,
  unlocked,
  onSave,
  onClear,
  saving,
}: {
  dateKey: string;
  dateLabel: string;
  highlight?: boolean;
  spend: number;
  sales: number;
  cpl: number | null;
  unlocked: boolean;
  onSave: (patch: { ad_spend: number; cpl: number | null }) => void;
  onClear: () => void;
  saving?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [spendStr, setSpendStr] = useState(spend ? String(spend) : "");
  const [cplStr, setCplStr] = useState(cpl != null ? String(cpl) : "");
  const hasData = spend > 0 || cpl != null;
  const roas = spend > 0 ? sales / spend : 0;

  const save = () => {
    const s = parseFloat(spendStr.replace(",", "."));
    const c = cplStr.trim() ? parseFloat(cplStr.replace(",", ".")) : null;
    onSave({
      ad_spend: Number.isFinite(s) ? s : 0,
      cpl: c != null && Number.isFinite(c) ? c : null,
    });
    setEditing(false);
  };

  return (
    <tr className={`text-cream/90 ${highlight ? "bg-gold/5" : ""}`}>
      <td className="px-4 py-3 font-semibold" title={dateKey}>{dateLabel}</td>
      <td className="px-4 py-3">
        {!unlocked ? (
          HIDDEN
        ) : editing ? (
          <input
            inputMode="decimal"
            value={spendStr}
            onChange={(e) => setSpendStr(e.target.value)}
            placeholder="0"
            className="w-24 rounded-md border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
          />
        ) : (
          brl(spend)
        )}
      </td>
      <td className="px-4 py-3">{brl(sales)}</td>
      <td className="px-4 py-3">
        {!unlocked ? (
          HIDDEN
        ) : editing ? (
          <input
            inputMode="decimal"
            value={cplStr}
            onChange={(e) => setCplStr(e.target.value)}
            placeholder="—"
            className="w-20 rounded-md border border-leaf/30 bg-deep/60 px-2 py-1 text-xs text-cream"
          />
        ) : cpl != null ? (
          brl(cpl)
        ) : (
          "—"
        )}
      </td>
      <td className="px-4 py-3">
        {!unlocked ? HIDDEN : spend > 0 ? `${roas.toFixed(2)}x` : "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          {!unlocked ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Lock className="h-3 w-3" /> bloqueado
            </span>
          ) : editing ? (
            <>
              <button
                onClick={save}
                disabled={saving}
                className="rounded-md border border-leaf/40 bg-leaf/20 px-2 py-1 text-[11px] font-semibold text-leaf-bright disabled:opacity-50"
              >
                Salvar
              </button>
              <button
                onClick={() => setEditing(false)}
                className="rounded-md border border-border px-2 py-1 text-[11px] text-cream/70"
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setSpendStr(spend ? String(spend) : "");
                  setCplStr(cpl != null ? String(cpl) : "");
                  setEditing(true);
                }}
                className="rounded-md border border-leaf/30 px-2 py-1 text-[11px] text-cream/80 hover:bg-leaf/10"
              >
                Editar
              </button>
              {hasData && (
                <button
                  onClick={onClear}
                  className="rounded-md border border-red-500/30 px-2 py-1 text-[11px] text-red-300 hover:bg-red-500/10"
                >
                  Limpar
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}


// ============================================================
// CRM META
// ============================================================

type CrmQuery = ReturnType<typeof useQuery<Awaited<ReturnType<typeof listCrmEvents>>>>;

const CRM_STAGES = ["Lead", "Contacted", "Qualified Lead", "Converted Lead"] as const;

function CrmPanel({ query }: { query: CrmQuery }) {
  const { data, isLoading, error } = query;
  const events = data?.events ?? [];
  const queryClient = useQueryClient();
  const resendFn = useServerFn(resendCrmEvent);
  const resendAllFn = useServerFn(resendFailedCrmEvents);
  const [stageFilter, setStageFilter] = useState<string>("todos");
  const [statusFilter, setStatusFilter] = useState<string>("todos");

  const resendOne = useMutation({
    mutationFn: (id: string) => resendFn({ data: { id } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["crm_events"] }),
  });
  const resendAll = useMutation({
    mutationFn: () => resendAllFn(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["crm_events"] }),
  });

  const filtered = useMemo(
    () =>
      events.filter(
        (e) =>
          (stageFilter === "todos" || e.stage === stageFilter) &&
          (statusFilter === "todos" || e.status === statusFilter),
      ),
    [events, stageFilter, statusFilter],
  );

  const onExport = () => {
    exportCsv("crm-meta-eventos", filtered, [
      ["created_at", "Data"],
      ["stage", "Estágio"],
      ["event_name", "Evento"],
      ["event_id", "Event ID"],
      ["email", "E-mail"],
      ["phone", "Telefone"],
      ["fbc", "fbc (click id Meta)"],
      ["fbp", "fbp"],
      ["redtrack_clickid", "ClickID RedTrack"],
      ["match_params_count", "Parâmetros"],
      ["value", "Valor"],
      ["status", "Status"],
      ["attempts", "Tentativas"],
      ["last_error", "Erro"],
    ]);
  };

  return (
    <>
      <section className="mt-6 grid gap-3 sm:grid-cols-4">
        <Stat label="Eventos CRM" value={data?.totalCount ?? 0} icon={<Users className="size-5" />} />
        <Stat label="Enviados" value={data?.sentCount ?? 0} />
        <Stat label="Pendentes" value={data?.pendingCount ?? 0} />
        <Stat label="Falhas" value={data?.failedCount ?? 0} />
      </section>

      <section className="mt-4 flex flex-wrap items-center gap-2">
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="rounded-xl border border-leaf/30 bg-card/60 px-3 py-2 text-xs text-cream"
        >
          <option value="todos">Todos os estágios</option>
          {CRM_STAGES.map((s) => (
            <option key={s} value={s}>
              {s} ({data?.byStage?.[s] ?? 0})
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-leaf/30 bg-card/60 px-3 py-2 text-xs text-cream"
        >
          <option value="todos">Todos os status</option>
          <option value="sent">Enviados</option>
          <option value="pending">Pendentes</option>
          <option value="failed">Falhas</option>
        </select>
        <button
          onClick={() => resendAll.mutate()}
          disabled={resendAll.isPending}
          className="inline-flex items-center gap-2 rounded-xl border border-leaf/40 bg-leaf/10 px-3 py-2 text-xs font-semibold text-cream transition hover:bg-leaf/20 disabled:opacity-50"
        >
          {resendAll.isPending ? <Loader2 className="size-4 animate-spin" /> : <RotateCw className="size-4" />}
          Reenviar pendentes/falhas
        </button>
        <button
          onClick={onExport}
          className="inline-flex items-center gap-2 rounded-xl border border-leaf/40 bg-leaf/10 px-3 py-2 text-xs font-semibold text-cream transition hover:bg-leaf/20"
        >
          <Download className="size-4" />
          Exportar CSV
        </button>
      </section>

      <section className="mt-4 overflow-hidden rounded-2xl border border-leaf/20 bg-card/60">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-cream/70">
            <Loader2 className="size-4 animate-spin" /> Carregando…
          </div>
        ) : error ? (
          <p className="p-10 text-center text-sm text-red-300">
            {(error as Error).message}
          </p>
        ) : filtered.length === 0 ? (
          <p className="p-10 text-center text-sm text-cream/60">Nenhum evento de CRM ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-xs">
              <thead className="bg-leaf/10 text-[10px] uppercase tracking-wider text-cream/70">
                <tr>
                  <th className="px-3 py-2">Data</th>
                  <th className="px-3 py-2">Estágio</th>
                  <th className="px-3 py-2">E-mail</th>
                  <th className="px-3 py-2">Click ID Meta (fbc)</th>
                  <th className="px-3 py-2">ClickID RedTrack</th>
                  <th className="px-3 py-2">Params</th>
                  <th className="px-3 py-2">Valor</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-leaf/10">
                {filtered.map((e) => (
                  <tr key={e.id as string} className="text-cream/90">
                    <td className="whitespace-nowrap px-3 py-2 text-cream/60">
                      {new Date(e.created_at as string).toLocaleString("pt-BR")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-semibold text-leaf-bright">
                      {e.stage as string}
                    </td>
                    <td className="px-3 py-2">{(e.email as string) ?? "—"}</td>
                    <td className="max-w-[220px] truncate px-3 py-2 font-mono text-[10px] text-cream/70">
                      {(e.fbc as string) ?? "—"}
                    </td>
                    <td className="max-w-[180px] truncate px-3 py-2 font-mono text-[10px] text-cream/70">
                      {(e.redtrack_clickid as string) ?? "—"}
                    </td>
                    <td className="px-3 py-2">{(e.match_params_count as number) ?? 0}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {e.value != null ? brl(Number(e.value)) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        title={(e.last_error as string) ?? (e.last_response as string) ?? ""}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          e.status === "sent"
                            ? "bg-leaf/20 text-leaf-bright"
                            : e.status === "failed"
                              ? "bg-red-500/20 text-red-300"
                              : "bg-amber-500/20 text-amber-300"
                        }`}
                      >
                        {e.status as string}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => resendOne.mutate(e.id as string)}
                        disabled={resendOne.isPending}
                        className="inline-flex items-center gap-1 rounded-lg border border-leaf/30 bg-leaf/5 px-2 py-1 text-[10px] font-semibold text-cream transition hover:bg-leaf/15 disabled:opacity-50"
                      >
                        <RotateCcw className="size-3" />
                        Reenviar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
